// Operaciones de administración (SPEC-005).
// Regla: ningún componente fuera de src/services/ puede llamar supabase.from() directo.
import { supabase } from './client';
import { SERVICE_STATUSES, TICKET_STATUSES, DOCUMENT_STATUSES, ROLES } from '../../constants';

/**
 * Perfiles por lista de ids.
 * @returns {{ data: Array|null, error: any }} como supabase
 */
export async function getProfilesByIds(ids, columns = 'id, name, email') {
  const uniqueIds = [...new Set((ids || []).filter(Boolean))];
  if (uniqueIds.length === 0) return { data: [], error: null };
  return supabase.from('profiles').select(columns).in('id', uniqueIds);
}

// ── Dashboard ────────────────────────────────────────────────────────────────

export async function fetchCompletedServicesToday(todayIso) {
  return supabase.from('services').select('*', { count: 'exact', head: true }).gte('created_at', todayIso).eq('status', SERVICE_STATUSES.COMPLETED);
}

export async function fetchEarningsSince(sinceIso) {
  return supabase.from('earnings').select('*').gte('created_at', sinceIso);
}

export async function fetchSearchingServicesCount() {
  return supabase.from('services').select('*', { count: 'exact', head: true }).eq('status', SERVICE_STATUSES.SEARCHING);
}

export async function fetchPendingDocsCount() {
  return supabase.from('driver_documents').select('*', { count: 'exact', head: true }).eq('status', DOCUMENT_STATUSES.PENDING);
}

export async function fetchOpenTicketsCount() {
  return supabase.from('support_tickets').select('*', { count: 'exact', head: true }).in('status', [TICKET_STATUSES.OPEN, TICKET_STATUSES.IN_PROGRESS]);
}

export async function getDriverRoleId() {
  return supabase.from('roles').select('id').eq('name', ROLES.DRIVER).maybeSingle();
}

export async function countUsersByRole(roleId) {
  return supabase.from('user_roles').select('user_id', { count: 'exact', head: true }).eq('role_id', roleId);
}

export async function fetchAllWalletsSummary() {
  return supabase.from('wallet').select('driver_uid, balance, currency');
}

// ── Servicios ────────────────────────────────────────────────────────────────

export async function fetchRecentServices(limit = 100) {
  return supabase.from('services').select('*').order('created_at', { ascending: false }).limit(limit);
}

export async function cancelService(serviceId, status = SERVICE_STATUSES.CANCELLED) {
  return supabase.from('services').update({ status }).eq('service_id', serviceId);
}

// ── Servicios programados ────────────────────────────────────────────────────

export async function fetchScheduledServices() {
  return supabase
    .from('scheduled_services')
    .select('*, profiles!scheduled_services_passenger_uid_fkey(name)')
    .order('pickup_time', { ascending: false });
}

export async function forceAssignScheduledService(id) {
  return supabase.rpc('auto_assign_scheduled_service', { sched_id: id });
}

// ── Alertas GPS / spoof ──────────────────────────────────────────────────────

export async function resetDriverSpoofState(driverUid) {
  await supabase
    .from('drivers_tracking')
    .update({ spoof_attempts: 0, last_spoof_alert: null, location_confidence: 'high' })
    .eq('driver_uid', driverUid);
  await supabase
    .from('drivers')
    .update({ status: 'active' })
    .eq('driver_uid', driverUid)
    .eq('status', 'under_review');
}

// ── Catálogo de servicios ────────────────────────────────────────────────────

export async function listServiceCategories() {
  return supabase.from('service_categories').select('*').order('sort_order').order('name');
}

export async function listServiceSubcategories() {
  return supabase.from('service_subcategories').select('*, category:category_id(name, slug)').order('category_id').order('sort_order').order('name');
}

export async function upsertCatalogEntry(table, payload) {
  return supabase.from(table).upsert(payload);
}

export async function deleteCatalogEntry(table, id) {
  return supabase.from(table).delete().eq('id', id);
}

export async function setCatalogEntryActive(table, id, isActive) {
  return supabase.from(table).update({ is_active: isActive }).eq('id', id);
}

// ── Turnos ───────────────────────────────────────────────────────────────────

export async function fetchAllDriverUids() {
  return supabase.from('drivers').select('driver_uid');
}

export async function fetchDriverProfilesOrdered(driverUids) {
  return supabase.from('profiles').select('id, name, email').in('id', driverUids).order('name');
}

// ── Tarifas ──────────────────────────────────────────────────────────────────

export async function fetchLatestTariffConfig() {
  return supabase.from('tariffs_config').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle();
}

export async function saveTariffConfig(config) {
  const { data: { user } } = await supabase.auth.getUser();
  return supabase.from('tariffs_config').upsert({ ...config, updated_at: new Date().toISOString(), updated_by: user?.id || null });
}

// ── Mapa en vivo ─────────────────────────────────────────────────────────────

export async function fetchActiveServices(statuses) {
  return supabase
    .from('services')
    .select('id, service_id, user_uid, driver_uid, pickup_lat, pickup_lng, dropoff_lat, dropoff_lng, pickup_address, dropoff_address, distance_km, price, fare, status, created_at')
    .in('status', statuses)
    .order('created_at', { ascending: false });
}

export async function fetchOnlineDrivers(trackingStatuses) {
  return supabase
    .from('drivers_tracking')
    .select('driver_uid, lat, lng, status, last_updated')
    .in('status', trackingStatuses)
    .not('lat', 'is', null)
    .not('lng', 'is', null);
}

export async function fetchActivePanicAlerts(activeStatus) {
  return supabase
    .from('panic_alerts')
    .select('id, user_id, service_id, lat, lng, status, created_at')
    .eq('status', activeStatus)
    .order('created_at', { ascending: false });
}

export async function resolvePanicAlert(alertId, resolvedStatus) {
  return supabase
    .from('panic_alerts')
    .update({ status: resolvedStatus, resolved_at: new Date().toISOString() })
    .eq('id', alertId);
}
